import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { all_routes } from '../../../../router/all_routes';
import CollapseHeader from '../../../../../core/common/collapse-header';
import { checkPermission } from '../../../../../core/permissons/CheckPermissons';
import apiDefinitions from '../../../../../core/api/apiDefinitions';
import {
  showSuccessAlert,
  showSuccessUpdateAlert,
  showDeleteConfirmation,
  showError,
  showItemExistsError,
  showItemAlreadyUsed,
} from '../../../../../core/alert/sweetAlertConfig';

import ItemBrandsTable from './ItemBrandsTable';
import ItemTypesTable from './ItemTypesTable';
import AddBrandModal from './AddBrandModal';
import EditBrandModal from './EditBrandModal';
import AddItemTypeModal, { AddItemTypePayload } from './AddItemTypeModal';
import EditItemTypeModal from './EditItemTypeModal';

import type { ItemBrand, ItemType } from './types';

const route = all_routes;

const navItems = [
  {
    label: 'Item Services',
    icon: 'ti ti-tools',
    link: all_routes.itemServiceCards,
    permission: 'ROLE_GET_ITEM_SERVICE_CARD',
  },
  { label: 'Items', icon: 'ti ti-box', link: all_routes.items, permission: 'ROLE_ADD_ITEM' },
  {
    label: 'Item Type & Brand',
    icon: 'ti ti-plus',
    link: all_routes.additemtypeandbrand,
    permission: 'ROLE_ADD_ITEM',
  },
];

const AddItemTypeAndBrandPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [itemBrands, setItemBrands] = useState<ItemBrand[]>([]);
  const [editBrand, setEditBrand] = useState<ItemBrand | null>(null);
  const [newBrand, setNewBrand] = useState({ brandName: '', description: '' });
  const [itemTypes, setItemTypes] = useState<ItemType[]>([]);
  const [editItemType, setEditItemType] = useState<ItemType | null>(null);
  const [newItemType, setNewItemType] = useState<{ itemType: string; description: string; checklistTemplate?: string }>({
    itemType: '',
    description: '',
  });
  const pageSize = 10;
  const [pageNo, setPageNo] = useState(1);

  const guardToken = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      showError('No authentication token found. Please log in.');
      navigate(route.login);
      return false;
    }
    return true;
  };

  const fetchItemBrands = async () => {
    if (!guardToken()) return;
    try {
      setLoading(true);
      const res = await apiDefinitions.getAllItemBrands(pageNo);
      if (res.data.status === 'S1000') setItemBrands(res.data.data || []);
      else showError(res.data.statusDescription || 'Failed to fetch item brands');
    } catch (e: any) {
      if (e.response?.status === 401) {
        showError('Unauthorized access. Your session may have expired. Please log in again.');
        localStorage.removeItem('token');
        navigate(route.login);
      } else showError(e.response?.data?.statusDescription || 'Error fetching item brands');
    } finally {
      setLoading(false);
    }
  };
  

  const fetchItemTypes = async () => {
    if (!guardToken()) return;
    try {
      setLoading(true);
      const res = await apiDefinitions.getAllItemTypes(pageNo);
      if (res.data.status === 'S1000') setItemTypes(res.data.data || []);
      else showError(res.data.statusDescription || 'Failed to fetch item types');
    } catch (e: any) {
      if (e.response?.status === 401) {
        showError('Unauthorized access. Your session may have expired. Please log in again.');
        localStorage.removeItem('token');
        navigate(route.login);
      } else showError(e.response?.data?.statusDescription || 'Error fetching item types');
    } finally {
      setLoading(false);
    }
  };

  const handleAddBrand = async () => {
    if (!newBrand.brandName.trim() || !newBrand.description.trim()) {
      showError('Brand name and description are required');
      return;
    }
    if (!guardToken()) return;
    try {
      setLoading(true);
      const res = await apiDefinitions.createItemBrand(newBrand);
      if (res.data.status === 'S1000') {
        showSuccessAlert("Item brand added successfully");
        setNewBrand({ brandName: '', description: '' });
        fetchItemBrands();
      } else showError(res.data.statusDescription || 'Failed to add item brand');
    } catch (e: any) {
      if (e.response?.status === 409) showItemExistsError("Item brand with this name already exists");
      else if (e.response?.status === 401) {
        showError('Unauthorized access. Your session may have expired. Please log in again.');
        localStorage.removeItem('token');
        navigate(route.login);
      } else showError(e.response?.data?.statusDescription || 'Error adding item brand');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateBrand = async () => {
    if (!editBrand || !editBrand.brandName.trim() || !editBrand.description.trim()) {
      showError('Brand name and description are required');
      return;
    }
    if (!guardToken()) return;
    try {
      setLoading(true);
      const res = await apiDefinitions.updateItemBrand(editBrand);
      if (res.data.status === 'S1000') {
        showSuccessUpdateAlert("Item brand updated successfully");
        setEditBrand(null);
        fetchItemBrands();
      } else showError(res.data.statusDescription || 'Failed to update item brand');
    } catch (e: any) {
      if (e.response?.status === 401) {
        showError('Unauthorized access. Your session may have expired. Please log in again.');
        localStorage.removeItem('token');
        navigate(route.login);
      } else showError(e.response?.data?.statusDescription || 'Error updating item brand');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBrand = (id: number) => {
    if (!guardToken()) return;
    showDeleteConfirmation(async () => {
      try {
        setLoading(true);
        const res = await apiDefinitions.deleteItemBrand(id);
        if (res.data.status === 'S1000') fetchItemBrands();
        else showError(res.data.statusDescription || 'Failed to delete item brand');
      } catch (e: any) {
        if (e.response?.status === 409) showItemAlreadyUsed("Item brand is associated with existing items and cannot be deleted.");
        else if (e.response?.status === 401) {
          showError('Unauthorized access. Your session may have expired. Please log in again.');
          localStorage.removeItem('token');
          navigate(route.login);
        } else showError(e.response?.data?.statusDescription || 'Error deleting item brand');
      } finally {
        setLoading(false);
      }
    });
  };

  const handleAddItemType = async (payload: AddItemTypePayload) => {
    if (!payload.itemType.trim() || !payload.description.trim()) {
      showError('Item type and description are required');
      return;
    }
    if (!guardToken()) return;
    try {
      setLoading(true);
      const res = await apiDefinitions.addItemType(payload);
      if (res.data.status === 'S1000') {
        showSuccessAlert("Item type added successfully");
        setNewItemType({ itemType: '', description: '', checklistTemplate: undefined });
        fetchItemTypes();
      } else showError(res.data.statusDescription || 'Failed to add item type');
    } catch (e: any) {
      if (e.response?.status === 409) showItemExistsError("Item type with this name already exists");
      else if (e.response?.status === 401) {
        showError('Unauthorized access. Your session may have expired. Please log in again.');
        localStorage.removeItem('token');
        navigate(route.login);
      } else showError(e.response?.data?.statusDescription || 'Error adding item type');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateItemType = async () => {
    if (!editItemType || !editItemType.itemType.trim() || !editItemType.description.trim()) {
      showError('Item type and description are required');
      return;
    }
    if (!guardToken()) return;
    try {
      setLoading(true);
      const res = await apiDefinitions.updateItemType(editItemType);
      if (res.data.status === 'S1000') {
        showSuccessUpdateAlert("Item type updated successfully");
        setEditItemType(null);
        fetchItemTypes();
      } else showError(res.data.statusDescription || 'Failed to update item type');
    } catch (e: any) {
      if (e.response?.status === 401) {
        showError('Unauthorized access. Your session may have expired. Please log in again.');
        localStorage.removeItem('token');
        navigate(route.login);
      } else showError(e.response?.data?.statusDescription || 'Error updating item type');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteItemType = (id: number) => {
    if (!guardToken()) return;
    showDeleteConfirmation(async () => {
      try {
        setLoading(true);
        const res = await apiDefinitions.deleteItemType(id);
        if (res.data.status === 'S1000') fetchItemTypes();
        else showError(res.data.statusDescription || 'Failed to delete item type');
      } catch (e: any) {
        if (e.response?.status === 409) showItemAlreadyUsed("Item type is associated with existing items and cannot be deleted.");
        else if (e.response?.status === 401) {
          showError('Unauthorized access. Your session may have expired. Please log in again.');
          localStorage.removeItem('token');
          navigate(route.login);
        } else showError(e.response?.data?.statusDescription || 'Error deleting item type');
      } finally {
        setLoading(false);
      }
    });
  };
const [viewItem, setViewItem] = useState<ItemBrand | ItemType | null>(null);

const handleViewItem = async (id: number, type: 'brand' | 'type') => {
  if (!guardToken()) return;
  try {
    setLoading(true);
    const res = await (type === 'brand' ? apiDefinitions.getItemBrandById(id) : apiDefinitions.getItemTypeById(id));
    if (res.data.status === 'S1000') setViewItem(res.data.data); // This will set the selected item for viewing
    else showError(res.data.statusDescription || 'Failed to fetch item');
  } catch (e: any) {
    if (e.response?.status === 401) {
      showError('Unauthorized access. Your session may have expired. Please log in again.');
      localStorage.removeItem('token');
      navigate(route.login);
    } else showError(e.response?.data?.statusDescription || 'Error fetching item');
  } finally {
    setLoading(false);
  }
};

  const openViewChecklist = (id: number) => {
    const itemType = itemTypes.find((t) => t.id === id);
    if (itemType) {
      openViewChecklist(id);
    }
  };

  useEffect(() => {
    fetchItemBrands();
    fetchItemTypes();
  }, [pageNo]);

  return (
    <div>
      <style>{`
        .truncate-name { max-width: 150px; word-wrap: break-word; white-space: normal; }
        .truncate-description { max-width: 250px; word-wrap: break-word; white-space: normal; }
      `}</style>

      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-8">
                    <h4 className="page-title">Facility Maintenance/Item Service/Item Type & Brand</h4>
                  </div>
                  <div className="col-4 text-end">
                    <div className="head-icons">
                      <CollapseHeader />
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="card-body pb-0 pt-2">
                    <ul className="nav nav-tabs nav-tabs-bottom" style={{ gap: '12px', display: 'flex' }}>
                      {navItems.map((item, index) =>
                        item.permission && checkPermission(item.permission) ? (
                          <li className="nav-item me-item" key={index}>
                            <Link
                              to={item.link}
                              className={`nav-link px-0 ${index === 2 ? 'active' : ''}`}
                            >
                              <i className={item.icon}></i> {item.label}
                            </Link>
                          </li>
                        ) : null
                      )}
                    </ul>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-lg-6 col-md-12">
                  <ItemBrandsTable
                    loading={loading}
                    brands={itemBrands}
                    pageNo={pageNo}
                    pageSize={pageSize}
                    onPrev={() => setPageNo(p => Math.max(1, p - 1))}
                    onNext={() => setPageNo(p => p + 1)}
                    onEdit={setEditBrand}
                    onView={(id) => handleViewItem(id, 'brand')}
                    onDelete={handleDeleteBrand}
                  />
                </div>


                <div className="col-lg-6 col-md-12">
                  <ItemTypesTable
                    loading={loading}
                    types={itemTypes}
                    pageNo={pageNo}
                    pageSize={pageSize}
                    onPrev={() => setPageNo((p) => Math.max(1, p - 1))}
                    onNext={() => setPageNo((p) => p + 1)}
                    onEdit={setEditItemType}
                    onView={async (id) => {
                      try {
                        setLoading(true);
                        const res = await apiDefinitions.getItemTypeById(id);
                        if (res.data.status !== 'S1000') {
                          showError(res.data.statusDescription || 'Failed to fetch item type');
                        }
                      } catch (e: any) {
                        if (e.response?.status === 401) {
                          showError('Unauthorized access. Your session may have expired. Please log in again.');
                          localStorage.removeItem('token');
                          navigate(route.login);
                        } else {
                          showError(e.response?.data?.statusDescription || 'Error fetching item type');
                        }
                      } finally {
                        setLoading(false);
                      }
                    }}
                    onDelete={handleDeleteItemType}
                    onViewChecklist={openViewChecklist}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AddBrandModal
        loading={loading}
        brandName={newBrand.brandName}
        description={newBrand.description}
        onChange={(p) => setNewBrand((prev) => ({ ...prev, ...p }))}
        onSave={handleAddBrand}
      />

      <EditBrandModal
        loading={loading}
        brand={editBrand}
        onChange={setEditBrand}
        onSave={handleUpdateBrand}
      />

      <AddItemTypeModal
        loading={loading}
        itemType={newItemType.itemType}
        description={newItemType.description}
        onChange={(p) => setNewItemType((prev) => ({ ...prev, ...p }))}
        onSave={handleAddItemType}
      />

      <EditItemTypeModal
  show={showModal}
  onHide={() => setShowModal(false)}
  onSuccess={() => { /* Handle success, e.g., refresh list */ }}
  itemType={editItemType}
  onChange={setEditItemType}
  onSave={handleUpdateItemType}
  loading={loading}
/>
    </div>
  );
};

export default AddItemTypeAndBrandPage;